import { FJEncodeMode, FJEncoderManager } from '@fj/codec';
import FJConfig from '../config/FJConfig';
import FJUtil from '../util/FJUtil';
import { updateDilation, updateEnlarge, updateFocus, updateResolution } from './FJFake3dRenderStore';
import { clamp } from 'lodash-es';
import { getAnimationData, getImageSize, tick, updateViewSize } from './FJFake3dStore';
import Download from '../lib/download';
import { QUALITIES } from '../config/dataConfig/FJFake3dData';
import FJConstant from '../constants/FJConstant';

let _blobInfo = {};
// const RESOLUTION_MAX_SIZE = {
//     120: 212,
//     240: 426,
//     480: 854,
//     720: 1280,
//     1080: 1920,
//     4000: 3840,
// };
const FJFake3dExportStore = {
    loadEncoderWorker() {
        const codecPath = `${FJConfig.libPath}codec/${FJConfig.codecVersion}/`;
        const wasmPath = `${FJConfig.libPath}wasm/${FJConfig.wasmVersion}/`;
        const subWorkerPath = FJUtil.getBrowser() === 'Safari' ? '' : `${codecPath}webcodec.worker.js`;
        FJEncoderManager.init(`${codecPath}encoder.worker.js`, subWorkerPath, wasmPath, `${wasmPath}codec.js`);
    },
    getExportVideoSize(size, resolution) {
        const ratio = size.width / size.height;
        let w = Number(resolution);
        let h = Number(resolution);
        if (ratio > 1) {
            w = h * ratio;
        } else {
            h = w / ratio;
        }
        const _evenNumber = num => {
            const newNum = Math.floor(num);
            return newNum % 2 === 0 ? newNum : newNum - 1;
        };

        return { width: _evenNumber(w), height: _evenNumber(h) };
    },
    updateViewSizeByResolution(resolution) {
        const imgSize = getImageSize();
        const exportSize = this.getExportVideoSize(imgSize, resolution);
        const { width, height } = exportSize;
        updateViewSize(width, height);
    },
    exportVideo(options) {
        const { formate = '', resolution, callback } = options;
        const imgSize = getImageSize();
        const exportSize = this.getExportVideoSize(imgSize, resolution);

        const { width, height } = exportSize;
        updateViewSize(width, height);

        const isExportGIF = formate.toLowerCase() === 'gif';
        const {
            startPoint,
            middlePoint,
            endPoint,
            animationDuration,
            amountOfMotion,
            amplitudePoint,
            phasePoint,
            enlarge,
            focus,
            edgeDilation,
            motionType,
            isLoop,
            isReverse,
        } = getAnimationData();
        // 1.编码所需数据
        const fps = isExportGIF ? 30 : 60;
        const isCircular = motionType === 'CIRCULAR';
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const totalFrames = Math.floor(animationDuration * fps);
        let encodeCount = 0;

        const timestampPerFrame = 1e6 / fps;

        const mode = !isExportGIF && FJUtil.couldUseWebCodecs('encode') ? FJEncodeMode.WEBCODECS : FJEncodeMode.FFMPEG;
        const quality = QUALITIES[resolution]?.mid || QUALITIES.default.mid;
        const videoConfig = {
            fps: fps || 30,
            width,
            height,
            presetIdx: 0,
            codecIdx: isExportGIF ? 2 : 1,
            formatIdx: isExportGIF ? 2 : 1,
            loop: 0,
            mode,
            quality,
            bitrateMode: 'variable',
            exportQuality: 1080,
        };
        // 2.编码回调
        let nextTime = 0;

        const _encoderCallback = async msg => {
            switch (msg.type) {
                case 'initialized':
                    while (encodeCount < totalFrames) {
                        await FJEncoderManager.getValidFramesSize();
                        const time = await new Promise((resolve, reject) => {
                            if (nextTime > animationDuration) {
                                resolve(-1);
                                return;
                            }

                            const currentTime = nextTime;
                            try {
                                tick(canvas, {
                                    canvasSize: exportSize,
                                    timestamp: currentTime,
                                    motionType,
                                    animationDuration,
                                    startPoint,
                                    middlePoint,
                                    endPoint,
                                    isCircular,
                                    isLoop,
                                    isReverse,
                                    amplitudePoint,
                                    phasePoint,
                                    amountOfMotion,
                                });
                            } catch (e) {
                                reject(e);
                            }
                            nextTime = currentTime + 1 / fps;
                            resolve(currentTime);
                        });
                        if (time === -1) {
                            break;
                        }

                        let frameType = '';
                        let frame = null;
                        const couldOptFrame = FJUtil.couldOptFrame();
                        if (
                            isExportGIF ||
                            typeof VideoFrame === 'undefined' ||
                            FJUtil.getBrowser() === 'FF' ||
                            FJUtil.getBrowser() === 'Safari'
                        ) {
                            frame = couldOptFrame
                                ? FJUtil.getImageBitmap(canvas)
                                : canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
                            frameType = couldOptFrame ? 'IMAGEBITMAP' : 'IMAGEDATA';
                        } else {
                            frameType = 'VIDEOFRAME';

                            // eslint-disable-next-line no-undef
                            frame = new VideoFrame(canvas, {
                                alpha: 'discard',
                                displayWidth: canvas.width,
                                displayHeight: canvas.height,
                                duration: timestampPerFrame,
                                timestamp: timestampPerFrame * encodeCount,
                            });
                        }
                        encodeCount++;
                        FJEncoderManager.addVideoFrame({
                            type: frameType,
                            data: frame,
                        });
                    }
                    if (encodeCount === totalFrames) {
                        FJEncoderManager.markVideoFrameDecodeDone();
                    }
                    break;
                case 'done': {
                    const blob = new Blob([msg.data], { type: 'application/octet-stream' });
                    const mimeType = isExportGIF ? 'image/gif' : 'video/mp4';
                    const fileName = isExportGIF ? 'FlexClip 3D Image.gif' : 'FlexClip 3D Image.mp4';
                    _blobInfo = { blob, mimeType, fileName };
                    callback && callback({ type: FJConstant.SUCCESS, data: _blobInfo });
                    // Download(blob, fileName, mimeType);
                    this._clear();
                    break;
                }
                case 'error': {
                    // 输出失败
                    console.error(msg.error);

                    callback && callback({ type: FJConstant.ERROR });
                    break;
                }
                case 'encoded': {
                    const progress = clamp(encodeCount / totalFrames, 0, 1);
                    callback && callback({ type: FJConstant.PROGRESS, data: progress });
                    break;
                }
                default:
                    break;
            }
        };
        const encoderOptions = {
            initCallback: () => {
                _encoderCallback({ type: 'initialized' });
            },
            doneCallback: data => {
                _encoderCallback({ type: 'done', data });
            },
            chunkCallback: (data, isHeader) => {
                _encoderCallback({ type: 'chunk_buffer', data, isHeader });
            },
            errorCallback: (e, status) => {
                let message = e ? e.message : '';
                // 将编码器状态信息加入message中
                if (status) {
                    try {
                        const keys = Object.keys(status);
                        keys.forEach(key => {
                            message += `, ${key}=${JSON.stringify(status[key])}`;
                        });
                    } catch (e) {
                        console.log(e);
                    }
                }

                _encoderCallback({ type: 'error', error: message });
            },
            encodeCallback: () => {
                _encoderCallback({ type: 'encoded' });
            },
        };
        // 3.初始化编码器
        // 4.更新渲染尺寸
        updateResolution(width, height);
        // 5.更新放大倍数
        updateEnlarge(enlarge);
        // 6.更新焦点
        updateFocus(focus);
        // 7.更新边缘模糊
        updateDilation(edgeDilation);

        FJEncoderManager.initEncoder(videoConfig, null, encoderOptions);
    },
    cancelExport() {
        this._clear();
    },
    _clear() {
        FJEncoderManager.cancel();
    },
    download(isAutoDownload = true) {
        const { blob, fileName, mimeType } = _blobInfo;
        const tool = FJUtil.getToolName();
        isAutoDownload && FJUtil.eaLog('ai tool download click', { source: 'creation', tool });
        Download(blob, fileName, mimeType);
    },
    getBlobInfo() {
        return _blobInfo;
    },
};
export default FJFake3dExportStore;
